import axios from 'axios';

const API_URL = process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com';

export const fetchPrivacyPolicy = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/privacy-policy`);
    return {
      title: response.data.data.Text,
      content: response.data.data.Content
    };
  } catch (error) {
    console.error('Error fetching privacy policy:', error);
    throw error;
  }
};

export const fetchTermsAndConditions = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/terms-and-condition`);
    return {
     title: response.data.data.Title,
    content: response.data.data.Content
    };
  } catch (error) {
    console.error('Error fetching terms and conditions:', error);
    throw error;
  }
};

export const fetchRefundPolicy = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/refund-policy`);
    return {
     title: response.data.data.Title,
    content: response.data.data.Content
    };
  } catch (error) {
    console.error('Error fetching refund policy:', error);
    throw error;
  }
};

export interface FaqItem {
  id: number;
  documentId: string;
  question: string;
  answer: any[] | string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface FaqResponse {
  data: FaqItem[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export const fetchFaqs = async (page = 1, pageSize = 100, searchQuery = ''): Promise<FaqResponse> => {
  try {
    const params: Record<string, string | number> = {
      'pagination[page]': page,
      'pagination[pageSize]': pageSize,
      sort: 'createdAt:desc',
    };
    if (searchQuery.trim()) {
      params['filters[question][$containsi]'] = searchQuery.trim();
    }
    const response = await axios.get(`${API_URL}/api/faqs`, { params });
    const records = response.data?.data || [];
    const mapped = records.map((item: any) => ({
      id: item.id,
      documentId: item.documentId || item.attributes?.documentId || '',
      question:
        item.question ||
        item.attributes?.question ||
        item.attributes?.title ||
        item.attributes?.name ||
        item.answer?.[0]?.children?.[0]?.text?.slice(0, 60) ||
        'Untitled FAQ',
      answer: item.answer || item.attributes?.answer || [],
      createdAt: item.createdAt || item.attributes?.createdAt || '',
      updatedAt: item.updatedAt || item.attributes?.updatedAt || '',
      publishedAt: item.publishedAt || item.attributes?.publishedAt || '',
    }));
    return {
      data: mapped,
      meta: response.data.meta || { pagination: { page, pageSize, pageCount: 1, total: mapped.length } },
    };
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    throw error;
  }
};

export const fetchUserProfile = async (token: string) => {
  try {
    const response = await axios.get(`${API_URL}/api/users/me?populate=*`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId: number, data: any, token: string) => {
  try {
    const response = await axios.put(`${API_URL}/api/users/${userId}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const registerUser = async (data: { username: string; email: string; password: string }) => {
  try {
    const response = await axios.post(`${API_URL}/api/auth/local/register`, data);
    return response.data;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

export const loginUser = async (data: { identifier: string; password: string }) => {
      console.log( 'Logging in at: ${API_URL}/api/auth/local');
  try {
    const response = await axios.post(`${API_URL}/api/auth/local`, data);
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const logoutUser = () => {
  localStorage.removeItem('jwt');
  localStorage.removeItem('userId');
};

export const resetPassword = async (data: { code: string; password: string; passwordConfirmation: string }) => {
  try {
    const response = await axios.post(`${API_URL}/api/auth/reset-password`, data);
    return response.data;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

export const requestPasswordReset = async (email: string) => {
  try {
    const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
    return response.data;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    throw error;
  }
};

export const deleteUser = async (token: string) => {
  try {
    const response = await axios.delete(`${API_URL}/api/users-permissions/users/delete-account`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export interface SubscriptionPlan {
  id: number;
  documentId: string;
  name: string;
  stripePriceId: string;
  stripeProductId: string;
  amount: number;
  currency: string;
  duration: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

export interface CurrentSubscription {
  id: string;
  status: string;
  plan: {
    id: string;
    amount: number;
    interval: string;
    productName: string;
  };
  startDate: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean; // Added to match backend response
}

export const fetchSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  const token = localStorage.getItem('jwt');
  if (!token) throw new Error('No token found');

  try {
    const response = await fetch(`${API_URL}/api/subscription-plans`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to fetch subscription plans: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    const data = await response.json();

    return data.data.map((plan: any) => ({
      id: plan.id,
      documentId: plan.documentId,
      name: plan.name,
      stripePriceId: plan.stripe_price_id,
      stripeProductId: plan.stripe_product_id,
      amount: plan.amount,
      currency: plan.currency,
      duration: plan.duration,
      isActive: plan.is_active,
      createdAt: plan.createdAt,
      updatedAt: plan.updatedAt,
      publishedAt: plan.publishedAt,
    }));
  } catch (error) {
    console.error('Error in fetchSubscriptionPlans:', error);
    throw error;
  }
};

export const createSubscription = async (priceId: string): Promise<{ sessionId: string; subscriptionId?: string }> => {
  const token = localStorage.getItem('jwt');
  if (!token) throw new Error('No token found');

  const response = await fetch(`${API_URL}/api/subscriptions`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ priceId }),
  });

  if (!response.ok) {
    throw new Error('Failed to create subscription');
  }

  const data = await response.json();
  return {
    sessionId: data.clientSecret,
    subscriptionId: data.subscriptionId,
  };
};

export const verifySubscription = async (subscriptionId: string): Promise<void> => {
  const token = localStorage.getItem('jwt');
  if (!token) throw new Error('No token found');

  const response = await fetch(`${API_URL}/api/subscriptions/${subscriptionId}/verify`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to verify subscription');
  }
};

export const fetchCurrentSubscription = async (): Promise<CurrentSubscription | null> => {
  const token = localStorage.getItem('jwt');
  if (!token) throw new Error('No token found');

  try {
    const response = await fetch(`${API_URL}/api/subscriptions/current`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Failed to fetch current subscription: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    const data = await response.json();

    // Handle case when subscription is explicitly null
    if (!data || data.subscription === null) {
      return null;
    }

    const subscription = data.subscription;

    // Safely parse and return the subscription object
    return {
      id: subscription.id,
      status: subscription.status,
      plan: {
        id: subscription.plan.id,
        productName: subscription.plan.productName,
        amount: subscription.plan.amount,
        interval: subscription.plan.interval,
      },
      startDate: subscription.startDate,
      currentPeriodEnd: subscription.currentPeriodEnd,
      cancelAtPeriodEnd: subscription.cancel_at_period_end ?? false, // Default to false if not present
    };
  } catch (error) {
    console.error('Error in fetchCurrentSubscription:', error);
    throw error;
  }
};

export const cancelSubscription = async (): Promise<{ message: string; subscriptionStatus: string; cancelAt: string }> => {
  const token = localStorage.getItem('jwt');
  if (!token) throw new Error('No token found');

  const response = await fetch(`${API_URL}/api/subscriptions/cancel`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Failed to cancel subscription: ${response.status} ${response.statusText} - ${errorBody}`);
  }

  return response.json();
};

export interface ApplicationSettings {
  iosAppLink: string;
  androidAppLink: string;
  heroTitle: string;
  heroSubtitle: string;
  ctaTitle: string;
  ctaSubtitle: string;
  promoText: string;
}

export const fetchApplicationSettings = async (): Promise<ApplicationSettings> => {
  try {
    const response = await axios.get(`${API_URL}/api/application-setting`);
    const data = response.data.data;
    return {
      iosAppLink: data.iosAppLink || 'https://apps.apple.com/app/id6746769046',
      androidAppLink: data.androidAppLink || 'https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat',
      heroTitle: data.heroTitle || '',
      heroSubtitle: data.heroSubtitle || '',
      ctaTitle: data.ctaTitle || '',
      ctaSubtitle: data.ctaSubtitle || '',
      promoText: data.promoText || '',
    };
  } catch (error) {
    console.error('Error fetching application settings:', error);
    return {
      iosAppLink: 'https://apps.apple.com/app/id6746769046',
      androidAppLink: 'https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat',
      heroTitle: '',
      heroSubtitle: '',
      ctaTitle: '',
      ctaSubtitle: '',
      promoText: '',
    };
  }
};

// BMI Tracking APIs
export interface BMIData {
  height: number;
  gender: 'Male' | 'Female' | 'Other';
  date_of_birth: string;
  currentWeight: number;
  idealBmiWeight?: number;
}

export const fetchBMI = async (token: string) => {
  try {
    const response = await fetch(`${API_URL}/api/bmis?populate=*`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch BMI data');
    const data = await response.json();
    return data.data.length > 0 ? data.data[0] : null;
  } catch (error) {
    console.error('Error fetching BMI:', error);
    throw error;
  }
};

export const createBMI = async (bmiData: BMIData, token: string) => {
  try {
    const response = await fetch(`${API_URL}/api/bmis`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: bmiData }),
    });
    if (!response.ok) throw new Error('Failed to create BMI record');
    return response.json();
  } catch (error) {
    console.error('Error creating BMI:', error);
    throw error;
  }
};

export const updateBMI = async (bmiId: string, bmiData: Partial<BMIData>, token: string) => {
  try {
    const response = await fetch(`${API_URL}/api/bmis/${bmiId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: bmiData }),
    });
    if (!response.ok) throw new Error('Failed to update BMI record');
    return response.json();
  } catch (error) {
    console.error('Error updating BMI:', error);
    throw error;
  }
};

// Elimination Calendar APIs
export interface EliminationEntry {
  user?: string;
  Item_name?: string;
  date: string;
  log_type: 'Eliminated Item' | 'Added Item' | 'Daily Note' | 'Mood Log' | 'Period Log' | 'Weight Track';
  mood_rating?: 'Exhausted' | 'Drained' | 'Low' | 'Sluggish' | 'Neutral' | 'Balanced' | 'Energized' | 'Vibrant' | 'Thriving' | 'Ecstatic';
  period_start_date?: string;
  period_end_date?: string;
  weight_kg?: number;
  energy_level?: number;
  notes?: string;
}

const normalizeISODate = (dateInput: string) => {
  const d = new Date(dateInput);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const fetchEliminationCalendar = async (token: string, userId?: string) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Always populate relationships
    queryParams.append('populate', '*');
    
    // Filter by user if provided
    if (userId) {
      queryParams.append('filters[user][id][$eq]', userId);
    }
    
    // Get current month date range
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Format dates as YYYY-MM-DD
    const startDateStr = monthStart.toISOString().split('T')[0];
    const endDateStr = monthEnd.toISOString().split('T')[0];
    
    queryParams.append('filters[date][$gte]', startDateStr);
    queryParams.append('filters[date][$lte]', endDateStr);
    
    // Add sorting and pagination
    queryParams.append('sort', 'date:desc');
    queryParams.append('pagination[pageSize]', '100');
    
    const url = `${API_URL}/api/elimination-calendars?${queryParams.toString()}`;
    console.log('Fetching elimination calendar from:', url);
    
    const response = await fetch(url, {
      headers: { 
        Authorization: `Bearer ${token}`, 
        'Content-Type': 'application/json' 
      },
    });
    
    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Failed to fetch elimination calendar: ${response.status} - ${errorData}`);
    }
    
    const data = await response.json();
    console.log('Elimination calendar data:', data);
    
    // Transform the response data to match expected format
    const entries = (data.data || []).map((entry: any) => ({
      id: entry.id,
      documentId: entry.documentId,
      date: entry.date,
      log_type: entry.log_type,
      Item_name: entry.Item_name,
      mood_rating: entry.mood_rating,
      notes: entry.notes,
      createdAt: entry.createdAt,
      updatedAt: entry.updatedAt,
    }));
    
    return entries;
  } catch (error) {
    console.error('Error fetching elimination calendar:', error);
    throw error;
  }
};

export const createEliminationEntry = async (entry: EliminationEntry, token: string, userId?: string) => {
  try {
    const cleanEntry: any = {
      ...entry,
      date: normalizeISODate(entry.date),
    };

    if (entry.period_start_date) {
      cleanEntry.period_start_date = normalizeISODate(entry.period_start_date);
    }

    if (entry.period_end_date) {
      cleanEntry.period_end_date = normalizeISODate(entry.period_end_date);
    }

    if (entry.log_type === 'Weight Track' && entry.weight_kg != null) {
      cleanEntry.weight_kg = Number(entry.weight_kg);
    }

    const entryData = {
      data: {
        ...cleanEntry,
        user: userId, // Associate entry with user
      },
    };
    
    console.log('Creating elimination entry:', entryData);
    
    const response = await fetch(`${API_URL}/api/elimination-calendars`, {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${token}`, 
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify(entryData),
    });
    
    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Failed to create elimination entry: ${response.status} - ${errorData}`);
    }
    
    const result = await response.json();
    console.log('Created entry:', result);
    return result;
  } catch (error) {
    console.error('Error creating elimination entry:', error);
    throw error;
  }
};

export const updateEliminationEntry = async (entryId: string, entry: Partial<EliminationEntry>, token: string) => {
  try {
    const response = await fetch(`${API_URL}/api/elimination-calendars/${entryId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: entry }),
    });
    if (!response.ok) throw new Error('Failed to update elimination entry');
    return response.json();
  } catch (error) {
    console.error('Error updating elimination entry:', error);
    throw error;
  }
};

export const deleteEliminationEntry = async (entryId: string, token: string) => {
  try {
    const response = await fetch(`${API_URL}/api/elimination-calendars/${entryId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to delete elimination entry');
    return response.json();
  } catch (error) {
    console.error('Error deleting elimination entry:', error);
    throw error;
  }
};